/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

import { usePlayerControls2 } from "../hooks/PlayerControlsContext";
import { usePlayerControls } from "../hooks/usePlayerControls";
import { SpriteThumbnail, parseWebVTT } from "../parseWebVTT";
import "../player-style.css";
import { PlayerUIProps } from "./PlayerUI";

const SeekSlider = ({ videoRef, thumbnail }: PlayerUIProps) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedTime, setBufferedTime] = useState(0);
  const [hoverTime, setHoverTime] = useState(0);
  const sliderRef = useRef<HTMLInputElement>(null);
  const { tempTime } = usePlayerControls2();
  const { updateHistory, isPlaying, seek } = usePlayerControls(videoRef);
  const [sprites, setSprites] = useState<SpriteThumbnail[]>([]);
  const [currentSprite, setCurrentSprite] = useState<SpriteThumbnail | null>(null);

  useEffect(() => {
    if (videoRef.current && isPlaying) {
      const video = videoRef.current;
      const interval = setInterval(() => {
        updateHistory(Math.floor(video.currentTime).toString());
      }, 60000);

      return () => clearInterval(interval);
    }
  }, []);

  useEffect(() => {
    if (thumbnail) {
      const parsed = parseWebVTT(thumbnail.large);
      setSprites(parsed);
    }
  }, [thumbnail]);

  useEffect(() => {
    let sprite = sprites.find((s) => Math.floor(hoverTime) >= s.start && Math.floor(hoverTime) < s.end);

    if (!sprite && sprites.length > 0) {
      const hoverSec = Math.floor(hoverTime);
      sprite = [...sprites].sort((a, b) => Math.abs(a.start - hoverSec) - Math.abs(b.start - hoverSec))[0];
    }

    setCurrentSprite(sprite || null);
  }, [hoverTime, sprites]);

  useEffect(() => {
    setCurrentTime(tempTime);
  }, [tempTime]);

  const [elementSize, setElementSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (sliderRef.current) {
      const { width, height } = sliderRef.current.getBoundingClientRect();
      setElementSize({ width, height });
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateDuration = () => {
      setDuration(video.duration);
      setCurrentTime(video.currentTime);
    };

    const updateBuffered = () => {
      if (!video.buffered.length) return;
      const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      setBufferedTime(bufferedEnd);
    };

    video.addEventListener("timeupdate", updateDuration);
    video.addEventListener("loadedmetadata", updateDuration);
    video.addEventListener("progress", updateBuffered);

    if (video.readyState >= 3) {
      setCurrentTime(video.currentTime);
    }

    return () => {
      video.removeEventListener("timeupdate", updateDuration);
      video.removeEventListener("loadedmetadata", updateDuration);
      video.removeEventListener("progress", updateBuffered);
    };
  }, [videoRef]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = Number(e.target.value);
    setCurrentTime(newTime);
    const newTempTIme = newTime - currentTime;
    if (videoRef.current) {
      seek(Math.floor(newTempTIme));
      updateHistory(Math.floor(newTime).toString());
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLInputElement>) => {
    const slider = e.currentTarget;
    const mousePosition = e.nativeEvent.offsetX;
    const newTime = (mousePosition / slider.offsetWidth) * duration;
    setHoverTime(Math.floor(newTime));
  };

  const handleMouseOut = () => {
    setHoverTime(0);
  };

  const playedPercent = duration ? (currentTime / duration) * 100 : 0;
  const bufferedPercent = duration ? (bufferedTime / duration) * 100 : 0;

  const trackBackground = `
    linear-gradient(
      to right,
      #FF4B00 0%, 
      #FF4B00 ${playedPercent}%,
      #868586 ${playedPercent}%,
      #868586 ${bufferedPercent}%,
      #CFD2D2 ${bufferedPercent}%,
      #CFD2D2 100%
    ) 
  `;

  const thumbnailWidth = 300;

  const thumbnailPosition = (hoverTime / duration) * 100;

  const constrainedLeft = Math.min(
    Math.max(thumbnailPosition, thumbnailWidth / 2 / (elementSize.width / 100)),
    100 - thumbnailWidth / 2 / (elementSize.width / 100),
  );

  const formatTime = (seconds: number) => {
    if (isNaN(seconds)) return "00:00:00";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return [hrs, mins, secs].map((unit) => String(unit).padStart(2, "0")).join(":");
  };

  return (
    <div className="flex items-center gap-6" ref={sliderRef}>
      <div className="relative w-full">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step="0.1"
          value={currentTime}
          onChange={handleInputChange}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseOut}
          style={{
            background: trackBackground,
            appearance: "none",
            width: "100%",
            cursor: "pointer",
            outline: "none",
          }}
          className="player-seek-slider h-2 rounded-lg max-lg:h-[6px]"
        />
        {hoverTime > 0 && currentSprite && (
          <div
            className="absolute bottom-8"
            style={{
              left: `calc(${constrainedLeft}% - ${currentSprite.w / 2}px)`,
            }}
          >
            <div
              className="origin-bottom scale-150 py-1"
              style={{
                width: `${currentSprite.w}px`,
                height: `${currentSprite.h}px`,
                backgroundImage: `url(${currentSprite.url})`,
                backgroundPosition: `-${currentSprite.x}px -${currentSprite.y}px`,
                border: "2px solid white",
                borderRadius: "12px",
                pointerEvents: "none",
              }}
            ></div>
            <p className="mt-2 text-center">{formatTime(hoverTime)}</p>
          </div>
        )}
      </div>
      <div className="w-[80px] text-end">
        {videoRef?.current ? formatTime(videoRef.current.duration - currentTime) : "00:00:00"}
      </div>
    </div>
  );
};

export default SeekSlider;
